// src/components/upload/FileUpload.tsx
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';

import Cropper from 'react-easy-crop';
import { createPortal } from 'react-dom';
import './FileUpload.css';

interface FileUploadProps {
  onUpload: (file: File) => Promise<void>;
  children: React.ReactNode;
  aspect?: number;       // ← новое
  cropShape?: 'round' | 'rect'; // ← новое
}

const FileUpload: React.FC<FileUploadProps> = ({ onUpload, children, aspect = 1, cropShape = 'rect' }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup для object URLs
  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedImage = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!imageSrc || !croppedAreaPixels) {
        resolve(null);
        return;
      }

      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);


        const { width, height } = croppedAreaPixels;
        canvas.width = width;
        canvas.height = height;

        ctx.fillStyle = '#eff3f4'; // белый цвет
        ctx.fillRect(0, 0, width, height); // заливаем весь canvas


        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          width,
          height
        );

        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 🔹 Лимит: 1 МБ = 1 * 1024 * 1024 байт
    const MAX_SIZE = 5 * 1024 * 1024;

    if (file.size > MAX_SIZE) {
      toast.error('Размер файла не должен превышать 5 МБ.');
      // alert('Размер файла не должен превышать 1 МБ.');
      // Опционально: сбрось input, чтобы не остался "грязный" файл
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const url = URL.createObjectURL(file);
    setImageSrc(url);
  };

  const handleUpload = async () => {
    const blob = await createCroppedImage();
    if (blob) {
      const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
      await onUpload(croppedFile);
      setImageSrc(null);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />

      <div onClick={triggerFileInput}>
        {children}
      </div>

      {/* Рендерим кроппер напрямую в body */}
      {imageSrc &&
        createPortal(
          <div className="file-upload-modal-overlay">
            <div className="file-upload-modal-content">
              <div className="file-upload-crop-container">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  showGrid={false}
                  aspect={aspect}
                  cropShape={cropShape}
                />
              </div>
              <div className="file-upload-buttons">
                <button className="file-upload-btn file-upload-btn-save" onClick={handleUpload}>
                  Сохранить
                </button>
                <button
                  className="file-upload-btn file-upload-btn-cancel"
                  onClick={handleCancel}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default FileUpload;
